<script setup>
import { computed, ref } from "vue";
import BaseBubble from "next/message/bubbles/Base.vue";
import MessageMeta from "next/message/MessageMeta.vue";
import FormattedContent from "next/message/bubbles/Text/FormattedContent.vue";
import Icon from "next/icon/Icon.vue";
import GalleryView from "dashboard/components/widgets/conversation/components/GalleryView.vue";
import { useMessageContext } from "next/message/provider.js";
import { useSnakeCase } from "dashboard/composables/useTransformKeys";

const { attachments, content, contentAttributes, orientation } =
  useMessageContext();

const layouts = {
  1: ["a a a a a a"],
  2: ["a a a b b b"],
  3: ["a a a a b b", "a a a a c c"],
  4: ["a a a a b b", "a a a a c c", "a a a a d d"],
  5: ["a a a b b b", "a a a c c c", "d d d e e e"],
  6: ["a a b b c c", "d d e e f f"],
  7: ["a a a b b b", "c c d d e e", "f f f g g g"],
  8: ["a a a b b b", "c c d d e e", "f f g g h h"],
  9: ["a a b b c c", "d d e e f f", "g g h h i i"],
  10: ["a a a b b b", "c c d d e e", "f f g g h h", "i i i j j j"],
};

const group = computed(() => contentAttributes.value?.tgGroup || {});
const parts = computed(() => group.value.albumParts || []);
const attachmentById = computed(
  () =>
    new Map((attachments.value || []).map((item) => [Number(item.id), item])),
);
const items = computed(() => {
  const ordered = parts.value.map((part) => ({
    id: part.providerMessageId,
    state: part.state,
    attachment: attachmentById.value.get(Number(part.attachmentId)),
  }));
  if (ordered.length) return ordered.slice(0, 10);
  return (attachments.value || [])
    .slice(0, 10)
    .map((item) => ({ id: item.id, state: "ready", attachment: item }));
});
const rows = computed(() => layouts[items.value.length] || layouts[10]);
const albumClass = computed(() => {
  const count = items.value.length;
  if (count <= 2) return "aspect-[4/3]";
  if (count <= 4) return "aspect-[4/5]";
  if (count <= 6) return "aspect-[5/4]";
  return "aspect-[4/5] sm:aspect-square";
});
const albumStyle = computed(() => ({
  gridTemplateAreas: rows.value.map((row) => `"${row}"`).join(" "),
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gridTemplateRows: `repeat(${rows.value.length}, minmax(0, 1fr))`,
}));
const tileStyle = (index) => ({ gridArea: String.fromCharCode(97 + index) });
const unavailableLabel = "Изображение недоступно";
const loadingLabel = "Загрузка изображения";
const stateLabel = (state) =>
  state === "unavailable" ? unavailableLabel : loadingLabel;
const readyAttachments = computed(() =>
  items.value.flatMap((item) => (item.attachment ? [item.attachment] : [])),
);
const readyAttachmentsSnake = computed(() =>
  useSnakeCase(readyAttachments.value),
);
const selectedAttachment = ref(null);
const showGallery = ref(false);
const openGallery = (item) => {
  if (!item.attachment) return;
  selectedAttachment.value = item.attachment;
  showGallery.value = true;
};
const metaClass = computed(() =>
  orientation.value === "right" ? "justify-end" : "justify-start",
);
</script>

<template>
  <BaseBubble
    hide-meta
    class="overflow-hidden p-0"
    data-bubble-name="telegram-media-album"
  >
    <div
      v-if="items.length"
      class="grid w-full gap-0.5 overflow-hidden bg-n-solid-1"
      :class="albumClass"
      :style="albumStyle"
      data-telegram-media-album
    >
      <button
        v-for="(item, index) in items"
        :key="item.id"
        type="button"
        class="relative min-h-0 min-w-0 overflow-hidden border-0 bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="item.attachment ? 'cursor-pointer' : 'cursor-default'"
        :style="tileStyle(index)"
        :aria-label="
          item.attachment
            ? `Открыть изображение ${index + 1}`
            : 'Изображение загружается'
        "
        :disabled="!item.attachment"
        @click="openGallery(item)"
      >
        <img
          v-if="item.attachment"
          :src="item.attachment.dataUrl"
          alt=""
          class="absolute inset-0 size-full object-cover skip-context-menu"
        />
        <span
          v-else
          class="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center text-xs text-n-slate-10"
        >
          <Icon
            :icon="
              item.state === 'unavailable'
                ? 'i-lucide-image-off'
                : 'i-lucide-loader-circle'
            "
            class="size-5"
            :class="{ 'animate-spin': item.state !== 'unavailable' }"
          />
          {{ stateLabel(item.state) }}
        </span>
      </button>
    </div>
    <div v-if="content" class="px-4 pt-4">
      <FormattedContent :content="content" />
    </div>
    <MessageMeta :class="metaClass" class="px-4 pb-3 mt-2 text-n-slate-11" />
  </BaseBubble>
  <GalleryView
    v-if="showGallery && selectedAttachment"
    v-model:show="showGallery"
    :attachment="useSnakeCase(selectedAttachment)"
    :all-attachments="readyAttachmentsSnake"
    @close="() => (showGallery = false)"
  />
</template>
